'use client';

import { RefObject, useEffect, useState } from 'react';

const ITEM_SELECTOR = '[data-paged-carousel-item]';

/**
 * For fixed-width items, measures how many of them (plus gaps) fit into the
 * carousel's width, capped at `maxItemsPerPage`, so a page never overflows
 * the viewport. For fluid items it returns `maxItemsPerPage` unchanged.
 *
 * The item width is measured from the first rendered item rather than parsed
 * from the prop, so any CSS length unit works.
 */
export function useFittedItemsPerPage(
  viewportRef: RefObject<HTMLUListElement | null>,
  maxItemsPerPage: number,
  itemWidth?: string
) {
  const [fittedItemsPerPage, setFittedItemsPerPage] = useState(maxItemsPerPage);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport || !itemWidth) {
      return;
    }

    const updateFit = () => {
      const item = viewport.querySelector<HTMLElement>(ITEM_SELECTOR);
      const viewportWidth = viewport.clientWidth;

      if (!item || viewportWidth === 0) {
        return;
      }

      const itemWidthPx = item.getBoundingClientRect().width;

      if (itemWidthPx === 0) {
        return;
      }

      const gapPx = parseFloat(getComputedStyle(viewport).columnGap) || 0;
      const fitCount = Math.floor(
        (viewportWidth + gapPx) / (itemWidthPx + gapPx)
      );
      const nextValue = Math.min(Math.max(fitCount, 1), maxItemsPerPage);

      if (nextValue !== fittedItemsPerPage) {
        setFittedItemsPerPage(nextValue);
      }
    };

    updateFit();

    const resizeObserver = new ResizeObserver(updateFit);
    resizeObserver.observe(viewport);

    return () => resizeObserver.disconnect();
  }, [viewportRef, itemWidth, maxItemsPerPage, fittedItemsPerPage]);

  return itemWidth ? fittedItemsPerPage : maxItemsPerPage;
}
